import { Component, Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export type ProfileSetupPromptMode = 'KYC' | 'PROFILE';

export type KycPromptStatus =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'CLARIFICATION_REQUIRED'
  | 'REJECTED';

export interface ProfileSetupPromptData {
  mode: ProfileSetupPromptMode;
  kycReferenceNumber?: string | null;
  kycStatus?: KycPromptStatus;
}

interface KycCopy {
  variant: 'start' | 'resume' | 'review' | 'clarify' | 'rejected';
  iconName: string;
  title: string;
  body: string;
  /** When null, the dialog only shows the close/dismiss button (informational state). */
  ctaLabel: string | null;
  laterLabel: string;
  /** Show the 3-step "what happens next" list. */
  showSteps: boolean;
}

@Component({
  selector: 'app-profile-setup-prompt',
  templateUrl: './profile-setup-prompt.component.html',
  styleUrls: ['./profile-setup-prompt.component.css'],
})
export class ProfileSetupPromptComponent {
  mode: ProfileSetupPromptMode = 'PROFILE';
  kycReferenceNumber: string | null = null;
  kycStatus: KycPromptStatus = 'NOT_STARTED';

  constructor(
    private dialogRef: MatDialogRef<ProfileSetupPromptComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) data: ProfileSetupPromptData | null
  ) {
    if (data) {
      this.mode = data.mode || 'PROFILE';
      this.kycReferenceNumber = data.kycReferenceNumber || null;
      this.kycStatus = data.kycStatus || 'NOT_STARTED';
    }
    console.log('[KYC-Modal] opened with status:', this.kycStatus, 'ref:', this.kycReferenceNumber);
  }

  get isKycMode(): boolean {
    return this.mode === 'KYC';
  }

  /**
   * Per-status presentation. SUBMITTED / UNDER_REVIEW are informational —
   * no proceed CTA, just an acknowledgement button.
   */
  get copy(): KycCopy {
    switch (this.kycStatus) {
      case 'IN_PROGRESS':
        return {
          variant: 'resume',
          iconName: 'edit_note',
          title: 'Resume your KYC verification',
          body:
            "You've already started your KYC application but haven't submitted it yet. Pick up where you left off — it only takes a few minutes.",
          ctaLabel: 'Resume KYC',
          laterLabel: 'Remind me later',
          showSteps: true,
        };

      case 'SUBMITTED':
        return {
          variant: 'review',
          iconName: 'hourglass_empty',
          title: 'Your KYC is under review',
          body:
            'Thanks for submitting your KYC! Our compliance team is reviewing your application and will get back to you within 2 to 3 business days. You\'ll be notified by email and in your portal inbox once a decision is made.',
          ctaLabel: null,
          laterLabel: 'Got it',
          showSteps: false,
        };

      case 'UNDER_REVIEW':
        return {
          variant: 'review',
          iconName: 'pending_actions',
          title: 'Verification in progress',
          body:
            'Our compliance team is actively reviewing your KYC application. You\'ll receive an email and in-app notification as soon as a decision is made — typically within 2 to 3 business days.',
          ctaLabel: null,
          laterLabel: 'Got it',
          showSteps: false,
        };

      case 'CLARIFICATION_REQUIRED':
        return {
          variant: 'clarify',
          iconName: 'help_outline',
          title: 'Action required: clarification needed',
          body:
            'Our compliance team has requested some clarifications on your KYC application. Open your application to review their notes and respond.',
          ctaLabel: 'Open my KYC',
          laterLabel: 'Later',
          showSteps: false,
        };

      case 'REJECTED':
        return {
          variant: 'rejected',
          iconName: 'report_problem',
          title: 'Your KYC needs revision',
          body:
            'Your KYC application was not approved. Please review the feedback inside the application, update your details, and resubmit.',
          ctaLabel: 'Review & resubmit',
          laterLabel: 'Later',
          showSteps: false,
        };

      case 'NOT_STARTED':
      default:
        return {
          variant: 'start',
          iconName: 'verified_user',
          title: 'Verify your business to begin',
          body:
            'Welcome to Varuna Sentinels Marketplace. Before you can transact, please complete a quick KYC verification — it takes about 5 minutes.',
          ctaLabel: 'Start KYC',
          laterLabel: 'Remind me later',
          showSteps: true,
        };
    }
  }

  proceed() {
    this.dialogRef.close('proceed');
  }

  later() {
    this.dialogRef.close('later');
  }
}
